// Helpers for the user detail's "App User Info" tab: find the Cognito account
// behind a DB user and flatten its attributes for display.

/** Fields the tab shows in the "Account" block rather than as attributes. */
const ACCOUNT_FIELDS = new Set(['username', 'status', 'enabled', 'created', 'last_modified', 'groups', 'custom_attributes', 'tenant_id']);

/**
 * Picks the Cognito account for a DB user out of `/sam/users/search` results.
 * `user.cognitoid` stores the Cognito `sub`, so a row that has one matches by sub and nothing
 * else: a dead sub is a deleted account, never "the first account with the same e-mail" (S-60).
 * The e-mail is only for rows that were never linked (no cognitoid).
 */
export function pickCognitoUser<T extends { sub?: string; email?: string }>(
  candidates: T[],
  cognitoId?: string | null,
  email?: string | null,
): T | null {
  if (cognitoId) return candidates.find((c) => c.sub === cognitoId) ?? null;
  if (email) {
    const wanted = email.trim().toLowerCase();
    return candidates.find((c) => c.email?.trim().toLowerCase() === wanted) ?? null;
  }
  return null;
}

export type AppAccountState =
  | { kind: 'deleted' }
  | { kind: 'other-row'; userId: number }
  | { kind: 'unverified' }
  | { kind: 'own' };

/**
 * What the App User Info tab may do with the account that came back (S-60):
 *   deleted    the row has a cognitoid and no account carries it: nothing to manage;
 *   other-row  the account belongs to another DB row (`user_id` differs): shown as a pointer, no actions;
 *   unverified found by e-mail (the row has no cognitoid) and the result carries no `user_id`, so
 *              nothing says it is this row's account: shown read-only, no actions;
 *   own        it is this row's account.
 * Found by the stored `sub`, a result without `user_id` is still the row's own.
 */
export function classifyAppAccount(
  match: { user_id?: number | null } | null,
  rowUserId: number | null | undefined,
  hasCognitoId: boolean,
): AppAccountState | null {
  if (!match) return hasCognitoId ? { kind: 'deleted' } : null;
  const other = match.user_id;
  if (other != null && rowUserId != null && Number(other) !== Number(rowUserId)) {
    return { kind: 'other-row', userId: Number(other) };
  }
  if (other == null && !hasCognitoId) return { kind: 'unverified' };
  return { kind: 'own' };
}

/** Standard + custom (`custom:` prefixed) attributes, sorted, as display rows. */
export function cognitoAttributeRows(raw: Record<string, unknown>): Array<{ name: string; value: string }> {
  const rows: Array<{ name: string; value: string }> = [];
  for (const [name, value] of Object.entries(raw)) {
    if (ACCOUNT_FIELDS.has(name) || value === undefined || value === null) continue;
    rows.push({ name, value: String(value) });
  }
  for (const [name, value] of Object.entries((raw.custom_attributes as Record<string, unknown> | undefined) ?? {})) {
    rows.push({ name: `custom:${name}`, value: String(value) });
  }
  return rows.sort((a, b) => a.name.localeCompare(b.name));
}
